import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Get follow-up history records
 * @param {Object} filter - MongoDB filter object
 * @param {number} page - Page number
 * @param {number} pageLimit - Items per page
 * @param {string} projection - Field projection
 * @param {Object} sort - Sort criteria
 * @returns {Promise} Follow-up history data
 */
export const getFollowUpHistory = (filter = {}, page = 1, pageLimit = 50, projection = '', sort = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_HISTORY,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(filter),
            page,
            pageLimit,
            projection,
            sort: JSON.stringify(sort || { date: -1 })
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create follow-up history record
 * @param {Object} data - History record data
 * @returns {Promise} Created history record
 */
export const createFollowUpHistory = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date().toISOString(),
          date: data.date || new Date().toISOString(),
          user: data.user || store.getState().auth.agiliteUser?.name || 'System',
          userId: data.userId || store.getState().auth.agiliteUser?._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_HISTORY,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(data)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record phone call activity
 * @param {Object} callData - Phone call data
 * @returns {Promise} Created history record
 */
export const recordPhoneCall = (callData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const historyRecord = {
          accountId: callData.accountId,
          action: 'Phone Call',
          type: 'phone',
          description: `${callData.purpose} - ${callData.description}${
            callData.duration ? ` (Duration: ${callData.duration} min)` : ''
          }`,
          outcome: callData.outcome,
          callDetails: {
            duration: callData.duration,
            purpose: callData.purpose,
            followUpActions: callData.followUpActions || []
          },
          notes: callData.notes
        }

        const response = await createFollowUpHistory(historyRecord)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record email communication
 * @param {Object} emailData - Email data
 * @returns {Promise} Created history record
 */
export const recordEmail = (emailData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const historyRecord = {
          accountId: emailData.accountId,
          action: 'Email Sent',
          type: 'email',
          description: emailData.subject,
          outcome: emailData.outcome || 'sent',
          emailDetails: {
            subject: emailData.subject,
            template: emailData.template,
            attachments: emailData.attachments || []
          },
          notes: emailData.notes
        }

        const response = await createFollowUpHistory(historyRecord)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record SMS communication
 * @param {Object} smsData - SMS data
 * @returns {Promise} Created history record
 */
export const recordSMS = (smsData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const historyRecord = {
          accountId: smsData.accountId,
          action: 'SMS Sent',
          type: 'sms',
          description: smsData.message.substring(0, 50) + '...',
          outcome: smsData.outcome || 'sent',
          smsDetails: {
            message: smsData.message,
            phoneNumber: smsData.phoneNumber
          },
          notes: smsData.notes
        }

        const response = await createFollowUpHistory(historyRecord)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record WhatsApp communication
 * @param {Object} whatsappData - WhatsApp data
 * @returns {Promise} Created history record
 */
export const recordWhatsApp = (whatsappData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const historyRecord = {
          accountId: whatsappData.accountId,
          action: 'WhatsApp Message',
          type: 'whatsapp',
          description: whatsappData.message.substring(0, 50) + '...',
          outcome: whatsappData.outcome || 'sent',
          whatsappDetails: {
            message: whatsappData.message,
            phoneNumber: whatsappData.phoneNumber,
            attachments: whatsappData.attachments || []
          },
          notes: whatsappData.notes
        }

        const response = await createFollowUpHistory(historyRecord)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record status change
 * @param {Object} statusData - Status change data
 * @returns {Promise} Created history record
 */
export const recordStatusChange = (statusData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const historyRecord = {
          accountId: statusData.accountId,
          action: 'Status Updated',
          type: 'status',
          description: `Changed from ${statusData.previousStatus} to ${statusData.newStatus}`,
          statusChange: {
            previousStatus: statusData.previousStatus,
            newStatus: statusData.newStatus,
            reason: statusData.reason
          },
          notes: statusData.notes
        }

        const response = await createFollowUpHistory(historyRecord)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get follow-up activity summary
 * @param {string} accountId - Account ID
 * @param {Object} dateRange - Date range filter
 * @returns {Promise} Activity summary
 */
export const getActivitySummary = (accountId, dateRange = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const pipeline = [
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              accountId
            }
          }
        ]

        // Add date range filter if provided
        if (dateRange.startDate && dateRange.endDate) {
          pipeline[0].$match.date = {
            $gte: dateRange.startDate,
            $lte: dateRange.endDate
          }
        }

        pipeline.push(
          {
            $group: {
              _id: '$type',
              count: { $sum: 1 },
              lastActivity: { $max: '$date' }
            }
          },
          {
            $group: {
              _id: null,
              totalActivities: { $sum: '$count' },
              activityBreakdown: {
                $push: {
                  type: '$_id',
                  count: '$count',
                  lastActivity: '$lastActivity'
                }
              }
            }
          }
        )

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_HISTORY,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )

        const summary = response.data[0] || {
          totalActivities: 0,
          activityBreakdown: []
        }

        resolve(summary)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete follow-up history record
 * @param {string} historyId - History record ID
 * @returns {Promise} Deletion result
 */
export const deleteFollowUpHistory = (historyId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_HISTORY,
          BillingEnums.routeKeys.DELETE,
          {
            recordId: historyId
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete all follow-up history (for testing purposes)
 * @returns {Promise}
 */
export const deleteAllFollowUpHistory = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Delete ALL follow-up history without any filter
        const filter = {}

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_HISTORY,
          BillingEnums.routeKeys.DELETE_MANY,
          {
            filter: JSON.stringify(filter)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
